// SPDX-License-Identifier: AGPL-3.0-or-later

export interface RoleReorderPoint {
	x: number;
	y: number;
}

export interface RoleReorderRect {
	top: number;
	bottom: number;
	left: number;
	right: number;
}

export interface RoleReorderAccess {
	canManageRoles: boolean;
	isGuildOwner: boolean;
}

export interface RoleReorderDragItem {
	id: string;
	isEveryone: boolean;
	isLocked: boolean;
}

interface RoleReorderTargetRole {
	id: string;
	isEveryone: boolean;
	isLocked: boolean;
}

export interface RoleReorderTarget {
	role: RoleReorderTargetRole | null;
	isTerminal: boolean;
}

type RoleReorderPosition = 'before' | 'after';

interface RoleReorderDropResult {
	targetRoleId: string | null;
	position: RoleReorderPosition;
}

interface RoleReorderIndicator {
	position: 'top' | 'bottom';
	isValid: boolean;
}

interface RoleReorderIntent {
	indicator: RoleReorderIndicator;
	result: RoleReorderDropResult;
}

type RoleReorderBlockedReason =
	| 'missing-manage-roles-permission'
	| 'source-is-everyone'
	| 'source-is-locked'
	| 'same-source-and-target'
	| 'target-before-locked-role'
	| 'top-target-requires-owner'
	| 'empty-target-rect';

export interface RoleReorderResolution {
	intent: RoleReorderIntent | null;
	indicator: RoleReorderIndicator | null;
	blockedReason: RoleReorderBlockedReason | null;
}

type VerticalZone = 'before' | 'after';

function getTargetHeight(rect: RoleReorderRect): number {
	return rect.bottom - rect.top;
}

function getVerticalZone(clientOffset: RoleReorderPoint, rect: RoleReorderRect): VerticalZone {
	const height = getTargetHeight(rect);
	const offsetY = Math.min(height, Math.max(0, clientOffset.y - rect.top));
	return offsetY < height / 2 ? 'before' : 'after';
}

function createIndicator(position: 'top' | 'bottom', isValid: boolean): RoleReorderIndicator {
	return {position, isValid};
}

function getSourceBlockedReason(item: RoleReorderDragItem, access: RoleReorderAccess): RoleReorderBlockedReason | null {
	if (!access.canManageRoles) return 'missing-manage-roles-permission';
	if (item.isEveryone) return 'source-is-everyone';
	if (item.isLocked && !access.isGuildOwner) return 'source-is-locked';
	return null;
}

function createDropResult(
	target: RoleReorderTarget,
	zone: VerticalZone,
): {indicatorPosition: 'top' | 'bottom'; result: RoleReorderDropResult} {
	if (target.role === null) {
		return {
			indicatorPosition: 'top',
			result: {
				targetRoleId: null,
				position: 'before',
			},
		};
	}
	if (target.isTerminal && !target.role.isEveryone && zone === 'after') {
		return {
			indicatorPosition: 'bottom',
			result: {
				targetRoleId: target.role.id,
				position: 'after',
			},
		};
	}
	return {
		indicatorPosition: 'top',
		result: {
			targetRoleId: target.role.id,
			position: 'before',
		},
	};
}

function getResultBlockedReason(
	item: RoleReorderDragItem,
	target: RoleReorderTarget,
	access: RoleReorderAccess,
	result: RoleReorderDropResult,
): RoleReorderBlockedReason | null {
	if (target.role === null && !access.isGuildOwner) return 'top-target-requires-owner';
	if (target.role && item.id === target.role.id) return 'same-source-and-target';
	if (
		target.role &&
		result.position === 'before' &&
		target.role.isLocked &&
		!target.role.isEveryone &&
		!access.isGuildOwner
	) {
		return 'target-before-locked-role';
	}
	return null;
}

function getRoleDropBlockedReason(
	item: RoleReorderDragItem,
	target: RoleReorderTarget,
	access: RoleReorderAccess,
): RoleReorderBlockedReason | null {
	const sourceBlockedReason = getSourceBlockedReason(item, access);
	if (sourceBlockedReason) return sourceBlockedReason;
	if (target.role === null && !access.isGuildOwner) return 'top-target-requires-owner';
	return null;
}

export function canRoleDropOnTarget(
	item: RoleReorderDragItem,
	target: RoleReorderTarget,
	access: RoleReorderAccess,
): boolean {
	return getRoleDropBlockedReason(item, target, access) === null;
}

function resolveRoleReorderHover(
	item: RoleReorderDragItem,
	target: RoleReorderTarget,
	access: RoleReorderAccess,
	clientOffset: RoleReorderPoint,
	targetRect: RoleReorderRect,
): RoleReorderResolution {
	if (getTargetHeight(targetRect) <= 0) {
		return {
			intent: null,
			indicator: null,
			blockedReason: 'empty-target-rect',
		};
	}
	const sourceBlockedReason = getSourceBlockedReason(item, access);
	const zone = getVerticalZone(clientOffset, targetRect);
	const {indicatorPosition, result} = createDropResult(target, zone);
	if (sourceBlockedReason) {
		return {
			intent: null,
			indicator: createIndicator(indicatorPosition, false),
			blockedReason: sourceBlockedReason,
		};
	}
	const resultBlockedReason = getResultBlockedReason(item, target, access, result);
	if (resultBlockedReason) {
		return {
			intent: null,
			indicator: createIndicator(indicatorPosition, false),
			blockedReason: resultBlockedReason,
		};
	}
	const indicator = createIndicator(indicatorPosition, true);
	return {
		intent: {
			indicator,
			result,
		},
		indicator,
		blockedReason: null,
	};
}

export function selectRoleReorderResolution(
	item: RoleReorderDragItem,
	target: RoleReorderTarget,
	access: RoleReorderAccess,
	clientOffset: RoleReorderPoint,
	targetRect: RoleReorderRect,
): RoleReorderResolution {
	return resolveRoleReorderHover(item, target, access, clientOffset, targetRect);
}
